import { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { adminApi } from '../lib/api'
import { ArrowLeft, Building2, AlertCircle } from 'lucide-react'

export default function TenantNew() {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const [formData, setFormData] = useState({
    company_name: '',
    slug: '',
    contact_email: '',
    tier: 'free' as 'free' | 'pro' | 'enterprise',
  })

  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
  }

  const handleCompanyNameChange = (value: string) => {
    setFormData({
      ...formData,
      company_name: value,
      slug: generateSlug(value),
    })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      const res = await adminApi.createTenant(formData)
      if (res.success) {
        navigate(`/tenants/${res.tenant.id}`)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create tenant')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Link
          to="/tenants"
          className="p-2 rounded-lg hover:bg-gray-800 text-gray-400 hover:text-white"
        >
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-white">New Tenant</h1>
          <p className="text-gray-400">Provision a new tenant organization</p>
        </div>
      </div>

      <div className="max-w-2xl">
        <div className="card p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-900/50 border border-red-700 rounded-lg text-red-300 text-sm">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                {error}
              </div>
            )}

            <div>
              <label htmlFor="company_name" className="label">
                Company Name *
              </label>
              <input
                id="company_name"
                type="text"
                value={formData.company_name}
                onChange={(e) => handleCompanyNameChange(e.target.value)}
                className="input"
                placeholder="Acme Corporation"
                required
              />
            </div>

            <div>
              <label htmlFor="slug" className="label">
                Slug *
              </label>
              <input
                id="slug"
                type="text"
                value={formData.slug}
                onChange={(e) =>
                  setFormData({ ...formData, slug: e.target.value })
                }
                className="input"
                placeholder="acme-corp"
                pattern="[a-z0-9-]+"
                required
              />
              <p className="mt-1 text-xs text-gray-500">
                Used for subdomain and resource naming. Lowercase letters, numbers, and hyphens only.
              </p>
            </div>

            <div>
              <label htmlFor="contact_email" className="label">
                Contact Email *
              </label>
              <input
                id="contact_email"
                type="email"
                value={formData.contact_email}
                onChange={(e) =>
                  setFormData({ ...formData, contact_email: e.target.value })
                }
                className="input"
                placeholder="admin@acme.com"
                required
              />
            </div>

            <div>
              <label htmlFor="tier" className="label">
                Subscription Tier
              </label>
              <select
                id="tier"
                value={formData.tier}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    tier: e.target.value as 'free' | 'pro' | 'enterprise',
                  })
                }
                className="input"
              >
                <option value="free">Free</option>
                <option value="pro">Pro</option>
                <option value="enterprise">Enterprise</option>
              </select>
            </div>

            {/* Preview */}
            <div className="p-4 bg-gray-900 rounded-lg border border-gray-700">
              <h3 className="text-sm font-medium text-gray-400 mb-3">
                Provisioning Preview
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-gray-500" />
                  <span className="text-gray-400">Subdomain:</span>
                  <span className="text-white font-mono">
                    {formData.slug || 'tenant'}.grc-platform.com
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-gray-400 ml-6">Worker:</span>
                  <span className="text-white font-mono">
                    grc-tenant-{formData.slug || 'slug'}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-gray-400 ml-6">Database:</span>
                  <span className="text-white font-mono">
                    tenant-{formData.slug || 'slug'}-db
                  </span>
                </div>
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <button
                type="submit"
                disabled={loading}
                className="btn-primary flex-1"
              >
                {loading ? 'Creating...' : 'Create Tenant'}
              </button>
              <Link to="/tenants" className="btn-secondary">
                Cancel
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
